import React, { useState, useEffect } from 'react';
import { Plus, Trash2, AlertCircle, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';

export interface ColumnConfig {
  displayLabel?: string; // Optional custom display label for the column
  options?: { value: string; label: string }[]; // For dropdown rendering
  readOnly?: boolean;
  type?: 'string' | 'auto'; // Force value type; 'auto' (default) infers number/boolean/null from input
}

export interface ColumnConfigs {
  [columnKey: string]: ColumnConfig;
}

interface JsonbTableEditorProps {
  value: string; // JSON string
  onChange: (value: string) => void;
  label: string;
  placeholder?: string;
  helperText?: string;
  columnConfigs?: ColumnConfigs; // Optional column-specific configurations
}

type TableData = Record<string, any>[];
type ViewMode = 'table' | 'raw';
type DataMode = 'array' | 'object' | 'primitive-array'; // array of objects, object, or array of primitives

export const JsonbTableEditor: React.FC<JsonbTableEditorProps> = ({
  value,
  onChange,
  label,
  placeholder = '[]',
  helperText,
  columnConfigs = {}
}) => {
  const { showToast } = useToast();
  const { t } = useTranslation();
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [dataMode, setDataMode] = useState<DataMode>('array');
  const [tableData, setTableData] = useState<TableData>([]);
  const [columns, setColumns] = useState<string[]>([]);
  const [rawJson, setRawJson] = useState<string>('');
  const [jsonError, setJsonError] = useState<string>('');
  const [showAddColumnModal, setShowAddColumnModal] = useState(false);
  const [newColumnName, setNewColumnName] = useState('');

  // Initialize data from value prop
  useEffect(() => {
    try {
      if (!value || value.trim() === '') {
        // Try to parse placeholder to determine data mode and extract columns
        try {
          const placeholderParsed = JSON.parse(placeholder);
          if (Array.isArray(placeholderParsed)) {
            if (placeholderParsed.length > 0 && typeof placeholderParsed[0] !== 'object') {
              // Primitive array
              setDataMode('primitive-array');
              setColumns(['value']);
            } else if (placeholderParsed.length > 0 && typeof placeholderParsed[0] === 'object' && placeholderParsed[0] !== null) {
              // Array of objects - extract columns from placeholder
              setDataMode('array');
              const placeholderKeys = Object.keys(placeholderParsed[0]);
              setColumns(placeholderKeys);
            } else {
              // Empty array or array with null
              setDataMode('array');
              setColumns([]);
            }
          } else if (typeof placeholderParsed === 'object' && placeholderParsed !== null) {
            setDataMode('object');
            setColumns(['key', 'value']);
          } else {
            setDataMode('array');
            setColumns([]);
          }
        } catch {
          setDataMode('array');
          setColumns([]);
        }
        setTableData([]);
        setRawJson(placeholder);
        setJsonError('');
        return;
      }

      const parsed = JSON.parse(value);

      if (Array.isArray(parsed)) {
        // Check if array contains primitives or objects
        const hasPrimitives = parsed.length > 0 && typeof parsed[0] !== 'object' || (parsed.length > 0 && parsed[0] === null);

        if (hasPrimitives) {
          // Array of primitives - convert to [{value: item}] format
          setDataMode('primitive-array');
          const primitiveData = parsed.map(item => ({ value: item }));
          setTableData(primitiveData);
          setColumns(['value']);
          setRawJson(JSON.stringify(parsed, null, 2));
          setJsonError('');
        } else if (parsed.length === 0) {
          // Empty array - try to get columns from placeholder
          setDataMode('array');
          setTableData([]);
          try {
            const placeholderParsed = JSON.parse(placeholder);
            if (Array.isArray(placeholderParsed) && placeholderParsed.length > 0 &&
                typeof placeholderParsed[0] === 'object' && placeholderParsed[0] !== null) {
              setColumns(Object.keys(placeholderParsed[0]));
            } else {
              setColumns([]);
            }
          } catch {
            setColumns([]);
          }
          setRawJson(JSON.stringify(parsed, null, 2));
          setJsonError('');
        } else {
          // Array of objects - extract all unique keys from ALL objects
          setDataMode('array');
          setTableData(parsed);

          const allKeys = new Set<string>();
          parsed.forEach(obj => {
            if (typeof obj === 'object' && obj !== null) {
              Object.keys(obj).forEach(key => allKeys.add(key));
            }
          });
          setColumns(Array.from(allKeys));
          setRawJson(JSON.stringify(parsed, null, 2));
          setJsonError('');
        }
      } else if (typeof parsed === 'object' && parsed !== null) {
        // Object mode - convert to key-value pairs
        setDataMode('object');
        const keyValuePairs = Object.entries(parsed).map(([key, value]) => ({
          key,
          value
        }));
        setTableData(keyValuePairs);
        setColumns(['key', 'value']);
        setRawJson(JSON.stringify(parsed, null, 2));
        setJsonError('');
      } else {
        // Primitive value - just show raw JSON
        setTableData([]);
        setColumns([]);
        setRawJson(value);
        setJsonError('');
      }
    } catch (e) {
      setJsonError((e as Error).message);
      setRawJson(value);
      setTableData([]);
      setColumns([]);
    }
  }, [value, placeholder]);

  // Update parent when table data changes
  const updateParent = (data: TableData, mode: DataMode) => {
    let jsonData: any;

    if (mode === 'object') {
      // Convert key-value pairs back to object
      jsonData = {};
      data.forEach(row => {
        if (row.key && row.key.trim() !== '') {
          jsonData[row.key] = parseValue(row.value);
        }
      });
    } else if (mode === 'primitive-array') {
      // Convert [{value: item}] back to [item, item, ...]
      jsonData = data.map(row => parseValue(row.value));
    } else {
      // Array mode - use data as is
      jsonData = data;
    }

    const jsonString = JSON.stringify(jsonData, null, 2);
    onChange(jsonString);
  };

  // Parse value with type detection
  const parseValue = (value: any, forceType?: 'string' | 'auto'): any => {
    if (value === null || value === undefined || value === '') {
      return null;
    }
    if (typeof value !== 'string') {
      return forceType === 'string' ? String(value) : value;
    }

    const trimmed = value.trim();
    if (trimmed === '') return null;
    if (forceType === 'string') return value;
    if (trimmed === 'true') return true;
    if (trimmed === 'false') return false;
    if (!isNaN(Number(trimmed)) && trimmed !== '') {
      return Number(trimmed);
    }
    return value;
  };

  // Handle cell value change
  const handleCellChange = (rowIndex: number, columnKey: string, newValue: string) => {
    const newData = [...tableData];
    if (!newData[rowIndex]) {
      newData[rowIndex] = {};
    }

    if (dataMode === 'object' || dataMode === 'primitive-array') {
      // In object or primitive-array mode, store raw value (parse later)
      newData[rowIndex][columnKey] = newValue;
    } else {
      // In array mode, parse the value (respecting column type config)
      const columnType = columnConfigs[columnKey]?.type;
      newData[rowIndex][columnKey] = parseValue(newValue, columnType);
    }

    setTableData(newData);
    updateParent(newData, dataMode);
  };

  // Add a new row
  const handleAddRow = () => {
    let newRow: Record<string, any>;

    if (dataMode === 'object') {
      // In object mode, add empty key-value pair
      newRow = { key: '', value: null };
    } else if (dataMode === 'primitive-array') {
      // In primitive-array mode, add empty value
      newRow = { value: '' };
    } else {
      // In array mode, add row with all columns
      newRow = {};
      columns.forEach(col => {
        newRow[col] = null;
      });
    }

    const newData = [...tableData, newRow];
    setTableData(newData);
    updateParent(newData, dataMode);
  };

  // Delete a row
  const handleDeleteRow = (rowIndex: number) => {
    const newData = tableData.filter((_, index) => index !== rowIndex);
    setTableData(newData);
    updateParent(newData, dataMode);
  };

  // Add a new column (only for array mode)
  const handleAddColumn = () => {
    if (!newColumnName.trim()) {
      return;
    }

    if (columns.includes(newColumnName)) {
      showToast(t('jsonbTable.columnAlreadyExists'), 'warning');
      return;
    }

    const newColumns = [...columns, newColumnName];
    setColumns(newColumns);

    // Add the new column to all existing rows with null value
    const newData = tableData.map(row => ({
      ...row,
      [newColumnName]: null
    }));

    setTableData(newData);
    updateParent(newData, dataMode);
    setNewColumnName('');
    setShowAddColumnModal(false);
  };

  // Delete a column (only for array mode)
  const handleDeleteColumn = (columnKey: string) => {
    const newColumns = columns.filter(col => col !== columnKey);
    setColumns(newColumns);

    // Remove the column from all rows
    const newData = tableData.map(row => {
      const newRow = { ...row };
      delete newRow[columnKey];
      return newRow;
    });

    setTableData(newData);
    updateParent(newData, dataMode);
  };

  // Format and validate JSON
  const handleFormatValidate = () => {
    try {
      const parsed = JSON.parse(rawJson);
      const formatted = JSON.stringify(parsed, null, 2);
      setRawJson(formatted);
      setJsonError('');
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  // Apply raw JSON to table
  const handleApplyToTable = () => {
    try {
      const parsed = JSON.parse(rawJson);

      if (Array.isArray(parsed)) {
        const hasPrimitives = parsed.length > 0 && typeof parsed[0] !== 'object';

        if (hasPrimitives) {
          // Array of primitives
          const primitiveData = parsed.map(item => ({ value: item }));
          setDataMode('primitive-array');
          setTableData(primitiveData);
          setColumns(['value']);
          setJsonError('');
          onChange(rawJson);
        } else {
          // Array of objects - extract columns
          const allKeys = new Set<string>();
          parsed.forEach(obj => {
            if (typeof obj === 'object' && obj !== null) {
              Object.keys(obj).forEach(key => allKeys.add(key));
            }
          });

          setDataMode('array');
          setTableData(parsed);
          setColumns(Array.from(allKeys));
          setJsonError('');
          onChange(rawJson);
        }
      } else if (typeof parsed === 'object' && parsed !== null) {
        // Convert object to key-value pairs
        const keyValuePairs = Object.entries(parsed).map(([key, value]) => ({
          key,
          value
        }));

        setDataMode('object');
        setTableData(keyValuePairs);
        setColumns(['key', 'value']);
        setJsonError('');
        onChange(rawJson);
      } else {
        setJsonError(t('jsonbTable.jsonMustBeArrayOrObject'));
      }
    } catch (e) {
      setJsonError((e as Error).message);
    }
  };

  // Switch to raw JSON from table
  const handleSwitchToRaw = () => {
    let jsonData: any;

    if (dataMode === 'object') {
      // Convert key-value pairs back to object
      jsonData = {};
      tableData.forEach(row => {
        if (row.key && row.key.trim() !== '') {
          jsonData[row.key] = parseValue(row.value);
        }
      });
    } else if (dataMode === 'primitive-array') {
      // Convert [{value: item}] back to [item, ...]
      jsonData = tableData.map(row => parseValue(row.value));
    } else {
      jsonData = tableData;
    }

    setRawJson(JSON.stringify(jsonData, null, 2));
    setViewMode('raw');
  };

  // Get display value for cell
  const getCellDisplayValue = (row: Record<string, any>, columnKey: string): string => {
    const value = row[columnKey];
    if (value === null || value === undefined) {
      return '';
    }
    if (typeof value === 'object') {
      return JSON.stringify(value);
    }
    return String(value);
  };

  // Get display label for column (supports custom labels via columnConfigs)
  const getColumnDisplayLabel = (columnKey: string): string => {
    const config = columnConfigs[columnKey];
    if (config?.displayLabel) {
      return config.displayLabel;
    }
    // Default: capitalize the column key
    return columnKey.charAt(0).toUpperCase() + columnKey.slice(1);
  };

  // Render input field for a cell (text input or dropdown based on config)
  const renderCellInput = (row: Record<string, any>, rowIndex: number, column: string) => {
    const config = columnConfigs[column];
    const cellValue = getCellDisplayValue(row, column);

    // If column has options, render as dropdown
    if (config?.options && config.options.length > 0) {
      return (
        <select
          value={cellValue}
          onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
          disabled={config.readOnly}
          className="input w-full"
        >
          <option value="">{t('jsonbTable.select')}</option>
          {config.options.map(option => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      );
    }

    // Default: render as text input
    return (
      <input
        type="text"
        value={cellValue}
        onChange={(e) => handleCellChange(rowIndex, column, e.target.value)}
        placeholder={
          dataMode === 'object' && column === 'key'
            ? t('jsonbTable.fieldName')
            : dataMode === 'primitive-array'
            ? t('jsonbTable.enterValue')
            : ''
        }
        disabled={config?.readOnly}
        className="input w-full"
      />
    );
  };

  return (
    <div className="space-y-3">
      {/* Label and Toggle */}
      <div className="flex items-center justify-between">
        <label className="block text-sm font-medium text-gray-700">
          {label}
        </label>

        {/* View Mode Toggle */}
        <div className="inline-flex rounded-lg border border-gray-300 bg-white p-1">
          <button
            type="button"
            onClick={() => setViewMode('table')}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'table'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            {t('jsonbTable.simpleTableView')}
          </button>
          <button
            type="button"
            onClick={handleSwitchToRaw}
            className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${
              viewMode === 'raw'
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:text-gray-900'
            }`}
          >
            {t('jsonbTable.rawJsonView')}
          </button>
        </div>
      </div>

      {/* Error Display */}
      {jsonError && (
        <div className="flex items-start gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="text-sm font-medium text-red-800">{t('jsonbTable.jsonError')}</p>
            <p className="text-sm text-red-700 mt-1 font-mono">{jsonError}</p>
          </div>
        </div>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
        <div className="space-y-4">
          {/* Column Headers with manage buttons (for array mode) */}
          {dataMode === 'array' && columns.length > 0 && (
            <div className="flex items-center gap-3 pb-2">
              {columns.map(column => (
                <div key={column} className="flex-1">
                  <div className="flex items-center gap-1.5">
                    <label className="text-sm font-medium text-gray-700">
                      {getColumnDisplayLabel(column)}
                    </label>
                    <button
                      type="button"
                      onClick={() => handleDeleteColumn(column)}
                      className="flex-shrink-0 text-red-600 hover:text-red-800 transition-colors"
                      title="Delete column"
                    >
                      <X className="w-3 h-3 stroke-current" />
                    </button>
                  </div>
                </div>
              ))}
              <div className="w-10"></div> {/* Spacer for trash column */}
            </div>
          )}

          {/* Primitive array header */}
          {dataMode === 'primitive-array' && (
            <div className="pb-2">
              <label className="block text-sm font-medium text-gray-700">{t('jsonbTable.value')}</label>
            </div>
          )}

          {/* Object mode header */}
          {dataMode === 'object' && (
            <div className="flex items-center gap-3 pb-2">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">{t('jsonbTable.key')}</label>
              </div>
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700">{t('jsonbTable.value')}</label>
              </div>
              <div className="w-10"></div> {/* Spacer for trash column */}
            </div>
          )}

          {/* Data Rows */}
          <div className="space-y-2">
            {tableData.length === 0 ? (
              <div className="text-center py-8 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-500">
                  {t('jsonbTable.noItemsYet', { type: dataMode === 'object' ? t('jsonbTable.field') : t('jsonbTable.item') })}
                </p>
              </div>
            ) : (
              tableData.map((row, rowIndex) => (
                <div key={rowIndex} className="flex items-start gap-3">
                  {/* Input fields in a row */}
                  {columns.map(column => (
                    <div key={column} className="flex-1">
                      {renderCellInput(row, rowIndex, column)}
                    </div>
                  ))}

                  {/* Delete button inline at the end */}
                  <button
                    type="button"
                    onClick={() => handleDeleteRow(rowIndex)}
                    className="flex-shrink-0 p-2 text-gray-400 hover:text-red-600 transition-colors mt-0.5"
                    title="Delete item"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))
            )}
          </div>

          {/* Action buttons */}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={handleAddRow}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
            >
              <Plus className="w-4 h-4" />
              {dataMode === 'object' ? t('jsonbTable.addField') : t('jsonbTable.addItem')}
            </button>

            {dataMode === 'array' && (
              <button
                type="button"
                onClick={() => setShowAddColumnModal(true)}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                <Plus className="w-4 h-4" />
                {t('jsonbTable.addColumn')}
              </button>
            )}
          </div>

          {/* Helper Text */}
          {helperText && (
            <p className="text-sm text-gray-500">{helperText}</p>
          )}
        </div>
      )}

      {/* Raw JSON View */}
      {viewMode === 'raw' && (
        <div className="space-y-3">
          <textarea
            value={rawJson}
            onChange={(e) => setRawJson(e.target.value)}
            placeholder={placeholder}
            rows={12}
            className="input w-full resize-none font-mono text-sm"
          />

          {/* Raw JSON Actions */}
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleFormatValidate}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              {t('jsonbTable.formatValidate')}
            </button>
            <button
              type="button"
              onClick={handleApplyToTable}
              className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
            >
              {t('jsonbTable.applyToTable')}
            </button>
          </div>

          {/* Helper Text */}
          {helperText && (
            <p className="text-sm text-gray-500">{helperText}</p>
          )}
        </div>
      )}

      {/* Add Column Modal (only for array mode) */}
      {showAddColumnModal && dataMode === 'array' && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {t('jsonbTable.addNewColumn')}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('jsonbTable.fieldName')}
                </label>
                <input
                  type="text"
                  value={newColumnName}
                  onChange={(e) => setNewColumnName(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleAddColumn();
                    }
                  }}
                  placeholder="e.g. weight, chest, etc."
                  className="input w-full"
                  autoFocus
                />
              </div>
              <div className="flex gap-2 justify-end">
                <button
                  type="button"
                  onClick={() => {
                    setShowAddColumnModal(false);
                    setNewColumnName('');
                  }}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  {t('common.cancel')}
                </button>
                <button
                  type="button"
                  onClick={handleAddColumn}
                  className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  {t('jsonbTable.addColumn')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
